import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Card, Empty, Spin, Typography } from 'antd';
import {
  buildTrainingCalendarMonth,
  sortProgressMonths,
  trainingMinuteIntensity,
} from './SimpleWorkspaceSupport';
import styles from './SimpleWorkspace.less';

const { Text } = Typography;

export default function TrainingPulsePanel({ monthlyProgress, loading }) {
  const sortedProgress = useMemo(
    () => sortProgressMonths(monthlyProgress),
    [monthlyProgress],
  );
  const months = useMemo(
    () => sortedProgress.map(buildTrainingCalendarMonth).filter(Boolean),
    [sortedProgress],
  );
  const latest = sortedProgress[sortedProgress.length - 1];
  const hasTraining = months.some((month) =>
    month.cells.some((cell) => (cell.minutes || 0) > 0),
  );
  const facts = [
    ['本月运动', latest?.totalSportMinute, '分钟'],
    ['活跃天数', latest?.totalDailyDay, '天'],
    ['最长连续', latest?.maxContinuousDay, '天'],
    ['单日峰值', latest?.highestTrainedDayMinutes, '分钟'],
  ];

  return (
    <Card
      size="small"
      bordered={false}
      title="训练连续性"
      extra={<Text className={styles.cardMeta}>近 6 个月每日训练分钟</Text>}
      className={`${styles.infoCard} ${styles.trainingPulseCard}`}
    >
      <Spin spinning={loading && !monthlyProgress.length}>
        {months.length ? (
          <>
            <div className={styles.pulseFacts}>
              {facts.map(([label, value, unit]) => (
                <div key={label} className={styles.pulseFact}>
                  <Text>{label}</Text>
                  <strong>{Number(value) || 0}</strong>
                  <small>{unit}</small>
                </div>
              ))}
            </div>
            {hasTraining ? (
              <div
                className={styles.trainingCalendarGrid}
                role="group"
                aria-label="近 6 个月每日训练时长热力图"
              >
                {months.map((month) => (
                  <div key={month.key} className={styles.trainingCalendarMonth}>
                    <div className={styles.trainingCalendarHeader}>
                      <strong>{month.label}</strong>
                      <span>一 二 三 四 五 六 日</span>
                    </div>
                    <div className={styles.trainingCalendarCells}>
                      {month.cells.map((cell) =>
                        cell.empty ? (
                          <span key={cell.key} aria-hidden="true" />
                        ) : (
                          <span
                            key={cell.key}
                            role="img"
                            className={`${styles.trainingCalendarCell} ${
                              styles[
                                `trainingCalendarLevel${trainingMinuteIntensity(
                                  cell.minutes,
                                )}`
                              ]
                            }`}
                            aria-label={`${month.label}.${String(
                              cell.day,
                            ).padStart(2, '0')}，训练 ${cell.minutes} 分钟`}
                            title={`${month.label}.${String(cell.day).padStart(
                              2,
                              '0',
                            )} · ${cell.minutes} 分钟`}
                          />
                        ),
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="近 6 个月暂无每日训练记录"
              />
            )}
            <div className={styles.trainingCalendarLegend}>
              <span>少</span>
              {[0, 1, 2, 3, 4].map((level) => (
                <i
                  key={level}
                  aria-hidden="true"
                  className={`${styles.trainingCalendarCell} ${
                    styles[`trainingCalendarLevel${level}`]
                  }`}
                />
              ))}
              <span>多</span>
            </div>
          </>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="暂无月度训练数据"
          />
        )}
      </Spin>
    </Card>
  );
}

TrainingPulsePanel.propTypes = {
  monthlyProgress: PropTypes.array,
  loading: PropTypes.bool,
};

TrainingPulsePanel.defaultProps = {
  monthlyProgress: [],
  loading: false,
};
